import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  Bolt,
  CheckCircle,
  Loader,
  RefreshCw,
  Tag,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Pin,
  Play,
  Square,
  Pencil,
  Volume2,
  Check,
  Trash2,
} from 'lucide-react';
import BlockStatusBadge from './BlockStatusBadge';
import BlockPlayground from './BlockPlayground';
import ComprehensiveDocPanel from './ComprehensiveDocPanel';
import ModelVoiceSelector from '../../../shared/ModelVoiceSelector';
import {
  generateBlocksDirect,
  generateTransitions,
  buildManifest,
  getManifest,
  consolidateBlocks,
  generateAudioForBlock,
} from './blockGeneratorDatabase';
import { ragService } from '../ragService';

// ─── Constants ───────────────────────────────────────────────────────────────

const SUPPORTED_LANGUAGES = ['en', 'pt', 'es', 'fr', 'de', 'bg'];

const DEPTH_OPTIONS = [
  { value: 'quick', label: 'Quick', description: 'up to 60 words' },
  { value: 'standard', label: 'Standard', description: 'up to 120 words' },
  { value: 'detailed', label: 'Detailed', description: 'up to 200 words' },
];

const TONE_OPTIONS = [
  { value: 'storytelling', label: 'Storytelling' },
  { value: 'casual', label: 'Casual' },
  { value: 'enthusiastic', label: 'Enthusiastic' },
  { value: 'educational', label: 'Educational' },
  { value: 'dramatic', label: 'Dramatic' },
  { value: 'humorous', label: 'Humorous' },
];

const AUDIENCE_OPTIONS = [
  { value: 'adults', label: 'Adults' },
  { value: 'children', label: 'Children' },
];


// ─── Helpers ─────────────────────────────────────────────────────────────────

function getItineraryId(itinerary) {
  return itinerary?.id || itinerary?.itineraryId || itinerary?.name || 'unknown';
}

function getPois(itinerary) {
  return itinerary?.points || itinerary?.stops || itinerary?.pois || [];
}

function getPoiId(poi, index) {
  return poi.id || poi.stopId || poi.poiId || `poi_${index}`;
}

function getPoiName(poi) {
  return poi.name || poi.title || poi.stopName || poi.poiName || `POI ${poi.id || 'unknown'}`;
}

function getPoiInDepthDescription(poi) {
  return poi.inDepthDescription || poi.in_depth_description || poi.description || '';
}

/**
 * Derive the RAG manifest URL from itinerary metadata via ragService.
 * Returns null if metadata is insufficient.
 */
function deriveRagManifestUrl(itinerary) {
  const metadata = itinerary?.metadata;
  if (!metadata?.country || !metadata?.name) return null;
  try {
    return ragService.generateManifestUrl(metadata);
  } catch {
    return null;
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

const BlockGeneratorManager = ({ itinerary, setItinerary, onStatusChange }) => {
  const itineraryId = getItineraryId(itinerary);
  const pois = getPois(itinerary);
  const ragManifestUrl = deriveRagManifestUrl(itinerary);

  // ── Global settings ──────────────────────────────────────────────────────
  const [globalLanguages, setGlobalLanguages] = useState(['en']);
  const [globalDepth, setGlobalDepth] = useState('quick');
  const [globalTones, setGlobalTones] = useState(['storytelling']);
  const [globalAudience, setGlobalAudience] = useState(['adults']);
  const [globalModel, setGlobalModel] = useState('gpt-4o-mini');
  // voice key (e.g. 'en-US-Gemini-Aoede-SimpleTTS') + resolved voiceId for backend ('Aoede')
  const [globalVoiceKey, setGlobalVoiceKey] = useState('en-US-Gemini-Aoede-SimpleTTS');
  const [globalVoiceId, setGlobalVoiceId] = useState('Aoede');
  const [globalVoiceConfig, setGlobalVoiceConfig] = useState({ useSimpleTtsMethod: true, model: 'gemini-3.1-flash-tts-preview' });
  const mandatoryBlockCount = 1; // fixed — not user-configurable

  // ── Per-POI config ───────────────────────────────────────────────────────
  // { [poiId]: { topics, status, blockCount, generatedBlocks } }
  const [poiConfigs, setPoiConfigs] = useState({});

  // ── Transitions ─────────────────────────────────────────────────────────
  const [transitionsStatus, setTransitionsStatus] = useState('none');
  const [generatedTransitions, setGeneratedTransitions] = useState([]);

  // ── Generation log ───────────────────────────────────────────────────────
  const [generationLog, setGenerationLog] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const logEndRef = useRef(null);

  // ── POI expand ───────────────────────────────────────────────────────────
  const [expandedPois, setExpandedPois] = useState({});

  // ── Block compositing enabled ─────────────────────────────────────────────
  const blockCompositingEnabled = itinerary?.metadata?.blockCompositing?.enabled ?? false;

  // ── Block consolidation ──────────────────────────────────────────────────
  const [consolidatingBlocks, setConsolidatingBlocks] = useState(false);

  // ── Per-block text editing ────────────────────────────────────────────────
  // { [blockId]: string } — edited transcript while in edit mode
  const [blockEdits, setBlockEdits] = useState({});
  // { [blockId]: boolean } — true while editing
  const [editingBlocks, setEditingBlocks] = useState({});
  // { [blockId]: boolean } — true while generating audio for that block
  const [generatingAudioBlocks, setGeneratingAudioBlocks] = useState({});

  // ── Audio playback ────────────────────────────────────────────────────────
  const [playingBlockId, setPlayingBlockId] = useState(null);
  const audioRef = useRef(null);

  const playBlock = useCallback((block) => {
    if (!block.publicUrl) { toast.error('No audio URL for this block'); return; }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const audio = new Audio(block.publicUrl);
    audioRef.current = audio;
    setPlayingBlockId(block.id);
    audio.play().catch((err) => { toast.error(`Playback failed: ${err.message}`); setPlayingBlockId(null); });
    audio.onended = () => setPlayingBlockId(null);
  }, []);

  const stopBlock = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingBlockId(null);
  }, []);

  const deleteBlock = useCallback((poiId, blockId, skipConfirm = false) => {
    if (!skipConfirm) {
      const block = poiConfigs[poiId]?.generatedBlocks?.find((b) => b.id === blockId);
      const label = block ? `"${block.topic}"` : 'this block';
      if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    }
    setPoiConfigs((prev) => {
      const blocks = (prev[poiId]?.generatedBlocks || []).filter((b) => b.id !== blockId);
      return { ...prev, [poiId]: { ...prev[poiId], generatedBlocks: blocks, blockCount: blocks.length } };
    });
  }, [poiConfigs]);

  // Generate audio for a single block (uses its current transcript, edited or original)
  const handleGenerateBlockAudio = useCallback(async (block, poiId) => {
    const transcript = blockEdits[block.id] ?? block.transcript;
    if (!transcript) { toast.error('No transcript to generate audio from.'); return; }

    setGeneratingAudioBlocks((p) => ({ ...p, [block.id]: true }));
    try {
      const result = await generateAudioForBlock(
        itineraryId,
        poiId,
        block.topic,
        transcript,
        block.language,
        ragManifestUrl,
        globalVoiceId,
        globalVoiceConfig?.useSimpleTtsMethod || false,
        globalVoiceConfig?.model
      );
      if (!result?.publicUrl) throw new Error('No URL returned');

      // Update the block in poiConfigs with the new publicUrl and transcript
      setPoiConfigs((prev) => ({
        ...prev,
        [poiId]: {
          ...prev[poiId],
          generatedBlocks: (prev[poiId]?.generatedBlocks || []).map((b) =>
            b.id === block.id
              ? { ...b, publicUrl: result.publicUrl, transcript }
              : b
          ),
        },
      }));
      // Clear any pending edit since it's now saved
      setEditingBlocks((p) => ({ ...p, [block.id]: false }));
      toast.success('Audio generated');
    } catch (err) {
      toast.error(`Audio generation failed: ${err.message}`);
    } finally {
      setGeneratingAudioBlocks((p) => ({ ...p, [block.id]: false }));
    }
  }, [itineraryId, ragManifestUrl, globalVoiceId, globalVoiceConfig, blockEdits]);

  // Stop audio on unmount
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  // ─── Auto-scroll log ─────────────────────────────────────────────────────
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [generationLog]);

  // ─── Auto-save to manifest (debounced, 3s after topics or blocks change) ──
  const manifestSaveTimer = useRef(null);
  const configsKey = useMemo(
    () => JSON.stringify(Object.fromEntries(
      Object.entries(poiConfigs).map(([id, cfg]) => [id, { t: cfg.topics || [], b: (cfg.generatedBlocks || []).length }])
    )),
    [poiConfigs]
  );
  useEffect(() => {
    if (!itineraryId || itineraryId === 'unknown') return;
    clearTimeout(manifestSaveTimer.current);
    manifestSaveTimer.current = setTimeout(async () => {
      const poisData = pois.map((poi, i) => {
        const poiId = getPoiId(poi, i);
        const config = poiConfigs[poiId] || {};
        return { poiId, poiName: getPoiName(poi), topics: config.topics || [], status: config.status || 'none', generatedBlocks: config.generatedBlocks || [] };
      });
      try {
        await buildManifest(itineraryId, poisData, {}, globalLanguages, ragManifestUrl);
      } catch (err) {
        console.warn('[BlockGenerator] Auto-save manifest failed:', err.message);
      }
    }, 3000);
    return () => clearTimeout(manifestSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configsKey, itineraryId]);

  // ─── Load existing blocks from manifest on mount ──────────────────────────
  useEffect(() => {
    if (!itineraryId || itineraryId === 'unknown') return;
    getManifest(itineraryId, ragManifestUrl)
      .then((data) => {
        const manifest = data?.manifest;
        if (!manifest?.pois) return;

        setPoiConfigs((prev) => {
          const updated = { ...prev };
          Object.entries(manifest.pois).forEach(([poiId, poiData]) => {
            // Flatten blocks across all languages, adding language field
            const allBlocks = Object.entries(poiData.blocks || {}).flatMap(
              ([lang, blocks]) => blocks.map((b) => ({ ...b, language: lang }))
            );
            updated[poiId] = {
              ...prev[poiId],
              topics: poiData.topics || prev[poiId]?.topics || [],
              status: allBlocks.length > 0 ? 'built' : 'none',
              blockCount: allBlocks.length,
              generatedBlocks: allBlocks,
            };
          });
          return updated;
        });

        if (manifest.transitions) {
          const allTransitions = Object.entries(manifest.transitions).flatMap(
            ([lang, items]) => items.map((t) => ({ ...t, language: lang }))
          );
          if (allTransitions.length > 0) {
            setGeneratedTransitions(allTransitions);
            setTransitionsStatus('built');
          }
        }
      })
      .catch((err) => {
        // 404 = manifest not yet built — silent. Anything else log it.
        if (!err.message?.includes('404') && !err.message?.includes('not found')) {
          console.warn('[BlockGenerator] Could not load manifest:', err.message);
        }
      });
  }, [itineraryId]);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const appendLog = useCallback((message) => {
    const ts = new Date().toLocaleTimeString();
    setGenerationLog((prev) => [...prev, `[${ts}] ${message}`]);
  }, []);

  const setPoiStatus = useCallback((poiId, status, blockCount, generatedBlocks) => {
    setPoiConfigs((prev) => ({
      ...prev,
      [poiId]: {
        topics: prev[poiId]?.topics || [],
        generatedBlocks: prev[poiId]?.generatedBlocks || [],
        blockCount: prev[poiId]?.blockCount || 0,
        ...prev[poiId],
        status,
        ...(blockCount != null ? { blockCount } : {}),
        ...(generatedBlocks != null ? { generatedBlocks } : {}),
      },
    }));
  }, []);

  // Combine selected tones + audience into a single string for the backend prompt
  const buildToneString = () => {
    const tones = globalTones.length > 0 ? globalTones.join(' and ') : 'storytelling';
    const audience = globalAudience.length > 0 ? `, tailored for ${globalAudience.join(' and ')}` : '';
    return `${tones}${audience}`;
  };

  const toggleTone = (value) =>
    setGlobalTones((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );

  const toggleAudience = (value) =>
    setGlobalAudience((prev) =>
      prev.includes(value) ? prev.filter((a) => a !== value) : [...prev, value]
    );

  const toggleLanguage = (lang) => {
    setGlobalLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    );
  };

  // ─── Generate blocks directly (blocks-first approach) ──────────────────

  const buildPoiDirect = useCallback(
    async (poi, poiIndex) => {
      const poiId = getPoiId(poi, poiIndex);
      const poiName = getPoiName(poi);
      const poiDesc = getPoiInDepthDescription(poi);
      const comprehensiveDocUrl = itinerary?.metadata?.comprehensiveDocUrl || null;

      if (!poiDesc && !comprehensiveDocUrl) {
        toast.error(`"${poiName}" needs a description or comprehensive document.`);
        return;
      }
      if (globalLanguages.length === 0) {
        toast.error('Select at least one language.');
        return;
      }
      if (!ragManifestUrl && !comprehensiveDocUrl) {
        const ok = window.confirm(
          `No research sources found.\n\nBlocks for "${poiName}" will use general LLM knowledge only.\n\nUpload a comprehensive document for richer content.\n\nProceed anyway?`
        );
        if (!ok) return;
      }

      setPoiStatus(poiId, 'building');
      appendLog(`Generating blocks for "${poiName}" in ${globalLanguages.join(', ')}…`);

      try {
        const result = await generateBlocksDirect(
          itineraryId,
          poiId,
          poiName,
          globalLanguages,
          globalDepth,
          buildToneString(),
          poiDesc,
          comprehensiveDocUrl,
          ragManifestUrl,
          10,
          mandatoryBlockCount
        );

        const successBlocks = (result?.blocks || []).filter((b) => !b.error);
        const failedCount = (result?.blocks || []).filter((b) => b.error).length;
        // Derive topic list from generated blocks
        const topics = successBlocks.filter(b => b.type === 'content').map(b => b.topic);

        if (successBlocks.length === 0 && failedCount > 0) {
          setPoiStatus(poiId, 'error');
          appendLog(`  "${poiName}" — all blocks failed.`);
          toast.error(`All blocks failed for "${poiName}"`);
        } else {
          setPoiConfigs((prev) => ({
            ...prev,
            [poiId]: {
              ...prev[poiId],
              topics,
              status: 'built',
              blockCount: successBlocks.length,
              generatedBlocks: successBlocks,
            },
          }));
          appendLog(`  "${poiName}" — ${successBlocks.length} blocks generated (text only).`);
          toast.success(`${successBlocks.length} blocks for "${poiName}"`);
        }
      } catch (err) {
        setPoiStatus(poiId, 'error');
        appendLog(`  "${poiName}" — ${err.message}`);
        toast.error(`Failed: ${err.message}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itinerary, itineraryId, globalLanguages, globalDepth, globalTones, globalAudience, ragManifestUrl]
  );

  // ─── Generate all POIs ────────────────────────────────────────────────────

  const handleGenerateAll = useCallback(async () => {
    if (globalLanguages.length === 0) { toast.error('Select at least one language.'); return; }
    setIsGenerating(true);
    appendLog(`=== Generating all POIs (${pois.length}) ===`);
    for (let i = 0; i < pois.length; i++) await buildPoiDirect(pois[i], i);
    appendLog('=== Done ===');
    setIsGenerating(false);
    onStatusChange?.('success', 'Block generation complete');
  }, [pois, buildPoiDirect, globalLanguages, appendLog, onStatusChange]);

  // ─── Generate audio for all blocks of a POI ────────────────────────────
  const handleGenerateAllAudio = useCallback(async (poi, poiIndex) => {
    const poiId = getPoiId(poi, poiIndex);
    const blocks = (poiConfigs[poiId]?.generatedBlocks || []).filter(b => !b.publicUrl && b.transcript);
    if (blocks.length === 0) { toast('All blocks already have audio.'); return; }

    appendLog(`Generating audio for ${blocks.length} blocks of "${getPoiName(poi)}"…`);
    let done = 0;
    for (const block of blocks) {
      try {
        await handleGenerateBlockAudio(block, poiId);
        done++;
      } catch { /* individual errors handled inside handleGenerateBlockAudio */ }
    }
    appendLog(`  Audio done: ${done}/${blocks.length}`);
  }, [poiConfigs, handleGenerateBlockAudio, appendLog]);

  // ─── Generate transitions ─────────────────────────────────────────────────

  const handleGenerateTransitions = useCallback(async () => {
    if (globalLanguages.length === 0) { toast.error('Select at least one language.'); return; }
    setTransitionsStatus('building');
    appendLog(`Generating transitions for ${globalLanguages.join(', ')}…`);
    try {
      const result = await generateTransitions(itineraryId, globalLanguages, ragManifestUrl);
      setGeneratedTransitions(result?.transitions || []);
      setTransitionsStatus('built');
      appendLog(`✓ Transitions generated (${result?.transitionsGenerated ?? 0}).`);
      toast.success('Transitions generated');
    } catch (err) {
      setTransitionsStatus('error');
      appendLog(`✗ Transitions — ${err.message}`);
      toast.error(`Transitions failed: ${err.message}`);
    }
  }, [itineraryId, globalLanguages, appendLog]);

  // ─── Consolidate blocks (transcript-level, cross-stop) ───────────────────

  const handleConsolidateBlocks = useCallback(async () => {
    // Gather all blocks across all POIs
    const allBlocks = pois.flatMap((poi, i) => {
      const poiId = getPoiId(poi, i);
      return (poiConfigs[poiId]?.generatedBlocks || []).map((b) => ({
        id: b.id,
        poiId,
        poiName: getPoiName(poi),
        topic: b.topic,
        type: b.type,
        transcript: b.transcript || '',
      }));
    }).filter((b) => b.transcript);

    if (allBlocks.length < 2) { toast('Need at least 2 generated blocks to consolidate.'); return; }

    setConsolidatingBlocks(true);
    appendLog(`Analyzing ${allBlocks.length} blocks for redundancy…`);
    try {
      const result = await consolidateBlocks(allBlocks);
      const verdicts = result?.results || [];
      const toRemove = verdicts.filter((v) => v.verdict === 'remove');

      if (toRemove.length === 0) {
        toast.success('No redundant blocks found — all blocks are unique!');
        appendLog('✓ No redundant blocks found.');
        return;
      }

      // Remove flagged blocks
      const removeIds = new Set(toRemove.map((v) => v.blockId));
      setPoiConfigs((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((poiId) => {
          const blocks = next[poiId]?.generatedBlocks || [];
          const filtered = blocks.filter((b) => !removeIds.has(b.id));
          if (filtered.length !== blocks.length) {
            next[poiId] = { ...next[poiId], generatedBlocks: filtered, blockCount: filtered.length };
          }
        });
        return next;
      });

      const reasons = toRemove.map((v) => `  • "${v.topic}" (${v.poiName}): ${v.reason}`).join('\n');
      appendLog(`✓ Removed ${toRemove.length} redundant blocks:\n${reasons}`);
      toast.success(`Removed ${toRemove.length} redundant blocks`);
    } catch (err) {
      toast.error(`Block consolidation failed: ${err.message}`);
      appendLog(`✗ Block consolidation — ${err.message}`);
    } finally {
      setConsolidatingBlocks(false);
    }
  }, [pois, poiConfigs, appendLog]);

  // ─── Rebuild manifest ─────────────────────────────────────────────────────

  const handleRebuildManifest = useCallback(async () => {
    appendLog('Building manifest…');
    const poisData = pois.map((poi, i) => {
      const poiId = getPoiId(poi, i);
      const config = poiConfigs[poiId] || {};
      return {
        poiId,
        poiName: getPoiName(poi),
        topics: config.topics || [],
        status: config.status || 'none',
        generatedBlocks: config.generatedBlocks || [],
      };
    });

    try {
      const result = await buildManifest(itineraryId, poisData, {}, globalLanguages, ragManifestUrl);
      const manifestUrl = result?.manifestUrl || '';
      appendLog(`✓ Manifest built${manifestUrl ? ': ' + manifestUrl : ''}`);
      toast.success('Manifest built');

      setItinerary((prev) => ({
        ...prev,
        metadata: {
          ...(prev?.metadata || {}),
          blockCompositing: {
            ...(prev?.metadata?.blockCompositing || {}),
            enabled: true,
            blockManifestUrl: manifestUrl,
          },
        },
      }));

      onStatusChange?.('success', 'Manifest rebuilt');
    } catch (err) {
      appendLog(`✗ Manifest — ${err.message}`);
      toast.error(`Manifest failed: ${err.message}`);
    }
  }, [itineraryId, pois, poiConfigs, globalLanguages, setItinerary, appendLog, onStatusChange]);

  // ─── Enable toggle ────────────────────────────────────────────────────────

  const toggleEnabled = () => {
    setItinerary((prev) => ({
      ...prev,
      metadata: {
        ...(prev?.metadata || {}),
        blockCompositing: {
          ...(prev?.metadata?.blockCompositing || {}),
          enabled: !blockCompositingEnabled,
        },
      },
    }));
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Bolt className="w-5 h-5 text-yellow-500" />
            Audio Blocks
          </h2>
          {(() => {
            const hasRag = !!ragManifestUrl;
            const hasDoc = !!itinerary?.metadata?.comprehensiveDocUrl;
            if (hasRag && hasDoc) return (
              <p className="mt-1 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                ✅ RAG manifest + comprehensive document — maximum research context for all blocks.
              </p>
            );
            if (hasDoc) return (
              <p className="mt-1 text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
                📄 Comprehensive document loaded — blocks will be enriched from it. Add focused RAG docs for even more detail.
              </p>
            );
            if (hasRag) return (
              <p className="mt-1 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                ✅ RAG manifest found — topic blocks will be enriched with research documents.
              </p>
            );
            return (
              <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-2">
                <span className="text-amber-500 mt-0.5">⚠️</span>
                <div className="text-sm text-amber-800">
                  <strong>No research sources.</strong> Upload a comprehensive document below or add RAG documents for richer blocks.
                </div>
              </div>
            );
          })()}
        </div>

        <button
          onClick={toggleEnabled}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            blockCompositingEnabled
              ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
              : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
          }`}
        >
          {blockCompositingEnabled ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-400" />}
          {blockCompositingEnabled ? 'Enabled' : 'Enable Block Compositing'}
        </button>
      </div>

      {/* Comprehensive Context Document */}
      <ComprehensiveDocPanel itinerary={itinerary} setItinerary={setItinerary} />

      {/* Global Settings */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Global Settings</h3>

        {/* Languages */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Languages</label>
          <div className="flex flex-wrap gap-2">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang}
                onClick={() => toggleLanguage(lang)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  globalLanguages.includes(lang)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Info note */}
        <p className="text-xs text-gray-400">
          Blocks are generated as text first. Audio (TTS) can be generated per-block or in batch after reviewing.
        </p>

        {/* Model + Voice */}
        <ModelVoiceSelector
          model={globalModel}
          voice={globalVoiceKey}
          onModelChange={setGlobalModel}
          onVoiceChange={(key, config) => {
            setGlobalVoiceKey(key);
            setGlobalVoiceId(config?.voiceId || key);
            setGlobalVoiceConfig(config || {});
          }}
        />

        {/* Depth + Tone + Audience */}
        <div className="grid grid-cols-3 gap-4">
          {/* Depth — radio (mutually exclusive) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Depth</label>
            <div className="flex flex-col gap-1">
              {DEPTH_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="depth" value={opt.value} checked={globalDepth === opt.value} onChange={() => setGlobalDepth(opt.value)} className="accent-blue-600" />
                  <span className="text-sm text-gray-700">{opt.label} <span className="text-xs text-gray-400">({opt.description})</span></span>
                </label>
              ))}
            </div>
          </div>

          {/* Tone — checkboxes (can combine multiple) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Tone <span className="text-gray-400 font-normal">(pick one or more)</span>
            </label>
            <div className="flex flex-col gap-1">
              {TONE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={globalTones.includes(opt.value)}
                    onChange={() => toggleTone(opt.value)}
                    className="accent-blue-600"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Audience — checkboxes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Audience <span className="text-gray-400 font-normal">(pick one or both)</span>
            </label>
            <div className="flex flex-col gap-1">
              {AUDIENCE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={globalAudience.includes(opt.value)}
                    onChange={() => toggleAudience(opt.value)}
                    className="accent-purple-600"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* POI List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Points of Interest ({pois.length})
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleConsolidateBlocks}
              disabled={isGenerating || consolidatingBlocks}
              className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-md border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {consolidatingBlocks ? <Loader className="w-3 h-3 animate-spin" /> : '✂️'}
              {consolidatingBlocks ? 'Analyzing…' : 'Consolidate all blocks'}
            </button>
          </div>
        </div>

        {pois.length === 0 && (
          <p className="text-sm text-gray-400 italic">No POIs found in this itinerary.</p>
        )}

        {pois.map((poi, index) => {
          const poiId = getPoiId(poi, index);
          const poiName = getPoiName(poi);
          const poiDesc = getPoiInDepthDescription(poi);
          const config = poiConfigs[poiId] || { topics: [], status: 'none', blockCount: 0, generatedBlocks: [] };
          const isExpanded = expandedPois[poiId] ?? true;
          const generatedBlocks = config.generatedBlocks || [];
          const blocksWithoutAudio = generatedBlocks.filter(b => !b.publicUrl && b.transcript);
          const hasMandatorySource = !!poiDesc;

          return (
            <div key={poiId} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              {/* Header row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <button onClick={() => setExpandedPois((p) => ({ ...p, [poiId]: !isExpanded }))} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                <span className="flex-1 text-sm font-medium text-gray-800 truncate">{poiName}</span>

                {/* Mandatory block indicator */}
                {hasMandatorySource && (
                  <span className="flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200">
                    <Pin className="w-2.5 h-2.5" />
                    {mandatoryBlockCount} mandatory
                  </span>
                )}

                <BlockStatusBadge status={config.status} blockCount={config.blockCount} />

                <button
                  onClick={() => buildPoiDirect(poi, index)}
                  disabled={config.status === 'building' || isGenerating}
                  className="flex items-center gap-1 px-3 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {config.status === 'building' ? <Loader className="w-3 h-3 animate-spin" /> : <Bolt className="w-3 h-3" />}
                  Generate Blocks
                </button>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                  {/* Mandatory block preview */}
                  {hasMandatorySource && (
                    <div className="bg-indigo-50 rounded-md border border-indigo-100 p-2.5">
                      <p className="text-xs font-medium text-indigo-700 flex items-center gap-1 mb-1">
                        <Pin className="w-3 h-3" />
                        Mandatory blocks source (inDepthDescription)
                      </p>
                      <p className="text-xs text-indigo-600 line-clamp-2">{poiDesc.slice(0, 160)}{poiDesc.length > 160 ? '…' : ''}</p>
                    </div>
                  )}

                  {/* Generated blocks */}
                  {generatedBlocks.length > 0 ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          <BookOpen className="w-3.5 h-3.5" />
                          Generated blocks ({generatedBlocks.length})
                          {blocksWithoutAudio.length > 0 && (
                            <span className="text-amber-500 ml-1">
                              — {blocksWithoutAudio.length} without audio
                            </span>
                          )}
                        </p>
                        {blocksWithoutAudio.length > 0 && (
                          <button
                            onClick={() => handleGenerateAllAudio(poi, index)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-purple-300 text-purple-600 hover:bg-purple-50 transition-colors"
                            title="Generate audio for all blocks without audio"
                          >
                            <Volume2 className="w-3 h-3" />
                            Generate all audio
                          </button>
                        )}
                      </div>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                          {generatedBlocks.map((block) => {
                            const isEditing = editingBlocks[block.id] || false;
                            const editedText = blockEdits[block.id] ?? block.transcript ?? '';
                            const isGeneratingAudio = generatingAudioBlocks[block.id] || false;
                            return (
                              <div
                                key={block.id}
                                className={`rounded-md border p-2.5 ${
                                  block.type === 'mandatory' ? 'bg-indigo-50 border-indigo-100'
                                  : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                {/* Block header row */}
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  {block.type === 'mandatory' ? (
                                    <Pin className="w-3 h-3 text-indigo-500" />
                                  ) : (
                                    <Tag className="w-3 h-3 text-blue-500" />
                                  )}
                                  <span className={`text-xs font-medium ${
                                    block.type === 'mandatory' ? 'text-indigo-700' : 'text-blue-700'
                                  }`}>
                                    {block.topic}
                                  </span>
                                  <span className="text-xs text-gray-400">{block.language?.toUpperCase()}</span>

                                  <div className="ml-auto flex items-center gap-1">
                                    {/* Play/stop if audio exists */}
                                    {block.publicUrl && (
                                      <button
                                        onClick={() => playingBlockId === block.id ? stopBlock() : playBlock(block)}
                                        className={`p-1 rounded-full transition-colors ${
                                          playingBlockId === block.id
                                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                            : 'bg-green-100 text-green-600 hover:bg-green-200'
                                        }`}
                                        title={playingBlockId === block.id ? 'Stop' : 'Play audio'}
                                      >
                                        {playingBlockId === block.id
                                          ? <Square className="w-2.5 h-2.5" />
                                          : <Play className="w-2.5 h-2.5" />}
                                      </button>
                                    )}

                                    {/* Edit text toggle */}
                                    <button
                                      onClick={() => {
                                        if (isEditing) {
                                          // Confirm edit — persist to poiConfigs
                                          const newText = blockEdits[block.id];
                                          if (newText != null && newText !== block.transcript) {
                                            setPoiConfigs((prev) => ({
                                              ...prev,
                                              [poiId]: {
                                                ...prev[poiId],
                                                generatedBlocks: (prev[poiId]?.generatedBlocks || []).map((b) =>
                                                  b.id === block.id ? { ...b, transcript: newText } : b
                                                ),
                                              },
                                            }));
                                          }
                                          setEditingBlocks((p) => ({ ...p, [block.id]: false }));
                                        } else {
                                          setBlockEdits((p) => ({ ...p, [block.id]: block.transcript ?? '' }));
                                          setEditingBlocks((p) => ({ ...p, [block.id]: true }));
                                        }
                                      }}
                                      className={`p-1 rounded transition-colors ${
                                        isEditing
                                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                      }`}
                                      title={isEditing ? 'Done editing' : 'Edit text'}
                                    >
                                      {isEditing ? <Check className="w-2.5 h-2.5" /> : <Pencil className="w-2.5 h-2.5" />}
                                    </button>

                                    {/* Generate audio button */}
                                    <button
                                      onClick={() => handleGenerateBlockAudio(
                                        { ...block, transcript: blockEdits[block.id] ?? block.transcript },
                                        poiId
                                      )}
                                      disabled={isGeneratingAudio}
                                      className="flex items-center gap-1 px-1.5 py-1 rounded text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      title={block.publicUrl ? 'Regenerate audio' : 'Generate audio'}
                                    >
                                      {isGeneratingAudio
                                        ? <Loader className="w-2.5 h-2.5 animate-spin" />
                                        : <Volume2 className="w-2.5 h-2.5" />}
                                    </button>

                                    {/* Delete block */}
                                    <button
                                      onClick={() => deleteBlock(poiId, block.id)}
                                      className="p-1 rounded transition-colors bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500"
                                      title="Delete block"
                                    >
                                      <Trash2 className="w-2.5 h-2.5" />
                                    </button>
                                  </div>
                                </div>

                                {/* Transcript — editable textarea or plain text */}
                                {isEditing ? (
                                  <textarea
                                    value={editedText}
                                    onChange={(e) => setBlockEdits((p) => ({ ...p, [block.id]: e.target.value }))}
                                    className="w-full text-xs text-gray-700 leading-relaxed border border-gray-300 rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y min-h-[80px]"
                                  />
                                ) : (
                                  <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                                    {blockEdits[block.id] ?? block.transcript}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">
                      Click "Generate Blocks" to create narration blocks from the source material.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Playground */}
      {(() => {
        const allBlocks = pois.flatMap((poi, i) => {
          const poiId = getPoiId(poi, i);
          return (poiConfigs[poiId]?.generatedBlocks || []).map(b => ({
            ...b,
            poiId,
            poiName: getPoiName(poi),
          }));
        });
        return <BlockPlayground allBlocks={allBlocks} transitions={generatedTransitions} onDeleteBlock={deleteBlock} />;
      })()}

      {/* Transitions */}
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Transition Clips</h3>
            <p className="text-xs text-gray-400 mt-0.5">Short "moving on" phrases played between blocks</p>
          </div>
          <div className="flex items-center gap-3">
            <BlockStatusBadge status={transitionsStatus} />
            <button
              onClick={handleGenerateTransitions}
              disabled={transitionsStatus === 'building' || isGenerating}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {transitionsStatus === 'building' ? <Loader className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Generate Transitions
            </button>
          </div>
        </div>
        {generatedTransitions.length > 0 && (
          <div className="space-y-1.5">
            {generatedTransitions.map((t) => (
              <div key={t.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <span className="text-xs text-gray-400 flex-shrink-0 uppercase">{t.language}</span>
                <span className="text-xs text-gray-700 flex-1">{t.transcript}</span>
                {t.publicUrl && (
                  <button
                    onClick={() => { const a = new Audio(t.publicUrl); a.play(); }}
                    className="flex-shrink-0 p-1 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600"
                    title="Preview"
                  >
                    <Play className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleGenerateAll}
          disabled={isGenerating}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating ? <Loader className="w-4 h-4 animate-spin" /> : <Bolt className="w-4 h-4" />}
          Generate All Blocks
        </button>

        <button
          onClick={handleRebuildManifest}
          disabled={isGenerating}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Rebuild Manifest
        </button>

        {itinerary?.metadata?.blockCompositing?.blockManifestUrl && (
          <a
            href={itinerary.metadata.blockCompositing.blockManifestUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View in Storage
          </a>
        )}
      </div>

      {/* Generation Log */}
      {generationLog.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-300">Generation Log</span>
            <button onClick={() => setGenerationLog([])} className="text-xs text-gray-400 hover:text-gray-200">Clear</button>
          </div>
          <div className="bg-gray-900 p-3 max-h-48 overflow-y-auto font-mono text-xs text-green-400 space-y-0.5">
            {generationLog.map((line, i) => <div key={i}>{line}</div>)}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockGeneratorManager;
